import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { emailOrPhone } from '@/utils/utils';
import isEmpty from 'lodash/isEmpty';
import classes from './EmailBadgeInput.module.scss';

const EMAIL_DELIMITERS = /[,\s\n;]+/;

const isValidEmail = (email) => emailOrPhone(email) === 'email';

const EmailBadgeInput = ({ onChange, placeholder = 'Enter email addresses...', className }) => {
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Report committed badges + any pending typed text on every change
  useEffect(() => {
    const all = [...emails];
    const p = inputValue.trim().toLowerCase();
    if (!isEmpty(p) && !all.includes(p)) all.push(p);
    onChangeRef.current?.(all);
  }, [emails, inputValue]);

  const addEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!isEmpty(trimmed)) {
      setEmails((prev) => {
        if (prev.includes(trimmed)) return prev;
        return [...prev, trimmed];
      });
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.endsWith(',') || value.endsWith(' ') || value.endsWith('\n')) {
      const candidate = value.slice(0, -1).trim();
      if (!isEmpty(candidate)) addEmail(candidate);
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isEmpty(inputValue)) {
        addEmail(inputValue);
        setInputValue('');
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    pasted
      .split(EMAIL_DELIMITERS)
      .filter((s) => !isEmpty(s))
      .forEach(addEmail);
  };

  return (
    <div className={`${classes.emailBadgeInput}${className ? ` ${className}` : ''}`} onClick={() => inputRef.current?.focus()}>
      {emails.map((email, i) => (
        <span key={i} className={`${classes.emailBadge} ${!isValidEmail(email) ? classes.emailBadgeInvalid : ''}`}>
          {email}
          <button
            type="button"
            className={classes.emailBadgeRemove}
            onClick={(e) => {
              e.stopPropagation();
              removeEmail(email);
            }}
            aria-label={`Remove ${email}`}
          >
            <FontAwesomeIcon icon={['fas', 'times']} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className={classes.emailBadgeInputField}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={emails.length === 0 ? placeholder : ''}
      />
    </div>
  );
};

export default EmailBadgeInput;
